/**
 * Open Food Facts client.
 *
 * OFF permits direct browser calls (it sends `Access-Control-Allow-Origin: *`)
 * and states that rate limits apply per end-user IP, so calling it from the
 * device is the sanctioned pattern rather than a workaround.
 *
 * LICENSING, which shapes the design: OFF data is ODbL, whose share-alike
 * terms attach to a redistributed DATABASE. Fetching rows at runtime for one
 * user is use, not redistribution — but bundling an OFF extract into the app
 * would not be. That is why the offline database shipped with this app is
 * USDA-only (public domain) and OFF results are stored separately, tagged
 * `source: 'off'`, with attribution shown in Settings.
 *
 * Limits: 15 requests/minute for product lookups, 10/minute for search.
 * A User-Agent identifying the app is requested by their policy.
 */
import type { FoodItem, Macros } from './schema';
import { newId } from '../lib/id';
import { nowIso } from '../lib/time';

const BASE = 'https://world.openfoodfacts.org';
const UA = 'Reta/1.0 (personal health tracker)';

/** Search uses the legacy endpoint on purpose: search.openfoodfacts.org sends no CORS headers. */
const SEARCH_URL = `${BASE}/cgi/search.pl`;

interface OffProduct {
  product_name?: string;
  brands?: string;
  code?: string;
  serving_size?: string;
  nutriments?: Record<string, number | string | undefined>;
}

function num(value: unknown): number | undefined {
  const n = typeof value === 'string' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? n : undefined;
}

/**
 * Read macros per 100 g.
 * Always the `_100g` fields: `_serving` values are only populated when OFF
 * managed to parse a serving size, and `serving_size` itself is free text.
 */
function macrosPer100g(n: Record<string, number | string | undefined> | undefined): Macros | null {
  if (!n) return null;
  const kcal = num(n['energy-kcal_100g']) ?? (num(n['energy_100g']) !== undefined ? num(n['energy_100g'])! / 4.184 : undefined);
  const protein = num(n.proteins_100g);
  const carbs = num(n.carbohydrates_100g);
  const fat = num(n.fat_100g);
  if (kcal === undefined || protein === undefined || carbs === undefined || fat === undefined) return null;

  const macros: Macros = {
    kcal: Math.round(kcal * 10) / 10,
    protein, carbs, fat,
  };
  const fiber = num(n.fiber_100g);
  if (fiber !== undefined) macros.fiber = fiber;
  const sugar = num(n.sugars_100g);
  if (sugar !== undefined) macros.sugar = sugar;
  // OFF reports sodium in GRAMS; the app stores milligrams.
  const sodiumG = num(n.sodium_100g);
  if (sodiumG !== undefined) macros.sodium = Math.round(sodiumG * 1000);
  return macros;
}

function toFoodItem(product: OffProduct, barcode?: string): FoodItem | null {
  const macros = macrosPer100g(product.nutriments);
  if (!macros) return null;
  const name = (product.product_name ?? '').trim();
  if (!name) return null;

  const item: FoodItem = {
    id: newId(),
    name,
    macros,
    refQty: 100,
    refUnit: 'g',
    source: 'off',
    createdAt: nowIso(),
  };
  const brand = (product.brands ?? '').split(',')[0]?.trim();
  if (brand) item.brand = brand;
  const code = barcode ?? product.code;
  if (code) { item.barcode = code; item.sourceId = code; }

  // serving_size is free text like "0.333 PACKAGE (52 g)" -- take the grams if present.
  const grams = /(\d+(?:\.\d+)?)\s*g\b/i.exec(product.serving_size ?? '');
  if (grams) item.portions = [{ label: 'serving', qty: 1, unit: 'serving', grams: Number(grams[1]) }];
  return item;
}

/** Look up a barcode. Returns null when OFF has no usable nutrition for it. */
export async function lookupBarcode(barcode: string, signal?: AbortSignal): Promise<FoodItem | null> {
  const clean = barcode.replace(/\D/g, '');
  if (!clean) return null;
  const res = await fetch(`${BASE}/api/v2/product/${clean}.json?fields=product_name,brands,code,serving_size,nutriments`, {
    headers: { Accept: 'application/json', 'User-Agent': UA },
    signal,
  });
  if (!res.ok) throw new Error(`Open Food Facts returned ${res.status}`);
  const body = (await res.json()) as { status?: number; product?: OffProduct };
  if (body.status !== 1 || !body.product) return null;
  return toFoodItem(body.product, clean);
}

/** Text search. Returns only products with usable macros. */
export async function searchProducts(query: string, limit = 12, signal?: AbortSignal): Promise<FoodItem[]> {
  const url = new URL(SEARCH_URL);
  url.searchParams.set('search_terms', query);
  url.searchParams.set('json', '1');
  url.searchParams.set('page_size', String(limit));
  url.searchParams.set('fields', 'product_name,brands,code,serving_size,nutriments');

  const res = await fetch(url.toString(), { headers: { Accept: 'application/json', 'User-Agent': UA }, signal });
  if (!res.ok) throw new Error(`Open Food Facts returned ${res.status}`);
  const body = (await res.json()) as { products?: OffProduct[] };
  return (body.products ?? []).map((p) => toFoodItem(p)).filter((x): x is FoodItem => x !== null);
}

export const OFF_ATTRIBUTION =
  'Product data from Open Food Facts, licensed under the Open Database License (ODbL).';
